import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useUpsertFeeScheduleMutation } from '../../../../../../../services/ContractService';
import { FeeScheduleView } from '../../../../../../../services/ContractService/types/contractTypes';
import { createDefaultFeeSchedule } from '../../../../../../../services/ContractService/createDefaultFeeSchedule';

interface FeeScheduleFormData {
    description: string;
    effectiveDate: string;
    endDate: string;
    feeType: string;
    feeSubType: string;
    category: string;
}

interface UseFeeScheduleFormProps {
    feeType: 'CPT' | 'DRG';
    open: boolean;
}

interface UseFeeScheduleFormResult {
    formData: FeeScheduleFormData;
    formErrors: { [key: string]: string };
    isLoading: boolean;
    navigationError: string | null;
    contractId: string;
    company: string;
    handleFormChange: (field: keyof FeeScheduleFormData, value: string) => void;
    handleSave: () => Promise<void>;
    clearNavigationError: () => void;
    resetForm: () => void;
}

export const useFeeScheduleForm = ({
    feeType,
    open
}: UseFeeScheduleFormProps): UseFeeScheduleFormResult => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const contractId = searchParams.get('id') || '';
    const company = searchParams.get('company') || '';
    const [navigationError, setNavigationError] = useState<string | null>(null);

    const [formData, setFormData] = useState<FeeScheduleFormData>({
        description: '',
        effectiveDate: '',
        endDate: '',
        feeType: feeType,
        feeSubType: feeType === 'DRG' ? 'Grouped' : 'Flat Rate',
        category: '',
    });
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
    
    const [upsertFeeSchedule, { isLoading }] = useUpsertFeeScheduleMutation();

    const resetForm = useCallback(() => {
        setFormData({
            description: '',
            effectiveDate: '',
            endDate: '',
            feeType: feeType,
            feeSubType: feeType === 'DRG' ? 'Grouped' : 'Flat Rate',
            category: '',
        });
        setFormErrors({});
    }, [feeType]);

    useEffect(() => {
        if (open) {
            resetForm();
        }
    }, [open, resetForm]);

    const handleFormChange = useCallback((field: keyof FeeScheduleFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (formErrors[field]) {
            setFormErrors(prev => ({ ...prev, [field]: '' }));
        }
    }, [formErrors]);

    const validateForm = useCallback(() => {
        const errors: { [key: string]: string } = {};

        if (!formData.description.trim()) {
            errors.description = 'Description is required';
        }

        if (!formData.feeType) {
            errors.feeType = 'Fee Type is required';
        }

        if (formData.effectiveDate && formData.endDate) {
            const effective = new Date(formData.effectiveDate);
            const end = new Date(formData.endDate);
            if (end < effective) {
                errors.endDate = 'End date must be after effective date';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData]);

    const handleSave = useCallback(async () => {
        if (!validateForm()) return;

        try {
            const feeScheduleData: FeeScheduleView = createDefaultFeeSchedule(contractId);

            feeScheduleData.description = formData.description.trim();
            feeScheduleData.feeType = formData.feeType;
            feeScheduleData.feeSubType = formData.feeSubType;
            feeScheduleData.category = formData.category.trim();
            feeScheduleData.effectiveDate = formData.effectiveDate || null;
            feeScheduleData.endDate = formData.endDate || null;

            const result = await upsertFeeSchedule({ feeSchedule: feeScheduleData }).unwrap();

            if (result?.id) {
                navigate(`/feeschedules/edit?company=${encodeURIComponent(company)}&feeScheduleId=${encodeURIComponent(result.id)}`);
            } else {
                setNavigationError('Fee schedule created but could not redirect. Please manually select it from the list.');
            }
        } catch (error) {
            console.error('Error saving fee schedule:', error);
            setFormErrors(prev => ({ ...prev, submit: 'Failed to save fee schedule. Please try again.' }));
        }
    }, [validateForm, contractId, formData, upsertFeeSchedule, navigate, company]);

    const clearNavigationError = useCallback(() => {
        setNavigationError(null);
    }, []);

    return {
        formData,
        formErrors,
        isLoading,
        navigationError,
        contractId,
        company,
        handleFormChange,
        handleSave,
        clearNavigationError,
        resetForm
    };
};
